import type { UTCDate } from '@date-fns/utc';
import { clamp, isAfter, startOfISOWeek } from 'date-fns';

import { getDateRangeFromPreset } from '../constants/datePresets';
import type {
  FilterConfig,
  FilterDefinition,
  FilterHandler,
} from '../engine/types';
import { type DateRangeFilter, FilterId, FilterType } from '../types/filters';

export const dateRangeDefinition: FilterDefinition<DateRangeFilter> = {
  id: FilterId.DATE_RANGE,
  name: 'Date Range',
  min: null,
  max: null,
  type: FilterType.DATE_RANGE,
  isActive: true,
};

const clampToSystemBounds = (
  date: UTCDate,
  systemMin: UTCDate,
  systemMax: UTCDate
): UTCDate => {
  return clamp(date, { start: systemMin, end: systemMax }) as UTCDate;
};

export const dateRangeHandler: FilterHandler<DateRangeFilter> = {
  isActive() {
    return true;
  },
  isDefault(
    currentFilterState: DateRangeFilter,
    definition: FilterDefinition<DateRangeFilter>
  ) {
    return (
      currentFilterState.min === definition.min &&
      currentFilterState.max === definition.max
    );
  },
  getEffectiveFilter(
    currentFilterState: DateRangeFilter,
    definition: FilterDefinition<DateRangeFilter>,
    config: FilterConfig
  ) {
    const { availableDateRange, viewGranularity, viewDateFilterId } = config;

    const isUserSubmitted =
      currentFilterState.isUserSubmitted === true ||
      !this.isDefault(currentFilterState, definition);

    if (!availableDateRange?.[viewGranularity]) {
      return {
        ...currentFilterState,
        min: null,
        max: null,
      };
    }

    const { min: systemMin, max: systemMax } =
      availableDateRange[viewGranularity];

    if (!systemMin || !systemMax) {
      console.warn(
        'dateRangeHandler.getEffectiveFilter: Invalid bounds for granularity',
        viewGranularity,
        { min: systemMin, max: systemMax }
      );
      return {
        ...currentFilterState,
        min: null,
        max: null,
      };
    }

    let finalStartDate: UTCDate;
    let finalEndDate: UTCDate;

    // Apply the template's preset default only while the user hasn't edited the
    // range. Once they submit their own dates, honor those so the filter stays
    // editable instead of snapping back to the preset.
    if (config.dateRangeDefault && !isUserSubmitted) {
      const overrideRange = getDateRangeFromPreset(
        config.dateRangeDefault,
        systemMax,
        systemMin
      );

      finalStartDate = overrideRange.start;
      finalEndDate = overrideRange.end;
    } else {
      finalStartDate = currentFilterState.min ?? systemMin;
      finalEndDate = currentFilterState.max ?? systemMax;
    }

    finalStartDate = clampToSystemBounds(finalStartDate, systemMin, systemMax);
    finalEndDate = clampToSystemBounds(finalEndDate, systemMin, systemMax);

    if (isAfter(finalStartDate, finalEndDate)) {
      console.warn(
        'dateRangeHandler.getEffectiveFilter: Invalid date range. Resetting to system defaults.',
        { start: finalStartDate, end: finalEndDate }
      );
      finalStartDate = systemMin;
      finalEndDate = systemMax;
    }

    if (viewGranularity === 'week') {
      finalStartDate = startOfISOWeek(finalStartDate) as UTCDate;
      finalEndDate = startOfISOWeek(finalEndDate) as UTCDate;
    }

    return {
      ...currentFilterState,
      min: finalStartDate,
      max: finalEndDate,
      isUserSubmitted,
      isVisible:
        viewDateFilterId === definition.id &&
        !this.isDefault(currentFilterState, definition),
    };
  },
};
